(() => {
  const normalize = (value) => value.toLowerCase().trim();
  const slug = (value) => normalize(value).replace(/&/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const aliases = { docks: 'docks & cradles', accessories: 'accessory', antennas: 'antenna', cables: 'cable' };
  const canonical = (value) => aliases[normalize(value)] || normalize(value);
  const groups = ['network', 'product_type', 'applications'];
  const sourceOrder = [233,178,177,172,165,158,154,156,214,30,29,34,31,24,23,26,33,32,18,19,21,141,22,14,16,15,224,225,226,183,181,182,173,27,77,169,51,50,52,35,36,37,38,116,209,208,203,204,206,207,76,230,215,74,117,39,167,40,41,42,43,44,45,46,47,48,49,118,119,123,120,121,85,79,78,152,151,80,81,150,82,83,84,86,87,88,89,90,229,235,236,237];

  class BeamCatalogue extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.checkboxes = [...this.querySelectorAll('[data-filter-group]')];
      this.searchInput = this.querySelector('input[name=q]');
      this.details = this.querySelector('.beam-catalogue-filters');
      this.status = this.querySelector('[data-catalogue-status]');
      this.limit = this.querySelector('[data-catalogue-limit]');
      this.pages = this.querySelector('[data-catalogue-pages]');
      this.grid = this.querySelector('.product-grid');
      this.setAttribute('aria-busy', 'true');
      this.checkboxes.forEach((input) => { input.disabled = true; });
      try {
        await this.loadCatalogue();
        if (this.dataset.sourceCatalogue === 'true') {
          const sourceProducts = new Map();
          [...this.grid.children].filter((element) => element.matches('[data-product-id]')).forEach((element) => {
            const source = element.dataset.sourceUrl?.match(/\/(\d+)-([^/?]+)/);
            const id = Number(source?.[1]);
            if (!sourceOrder.includes(id)) { element.remove(); return; }
            const previous = sourceProducts.get(id);
            if (previous) {
              if (element.dataset.handle === source[2]) previous.remove();
              else { element.remove(); return; }
            }
            sourceProducts.set(id, element);
          });
        }
        this.products = [...this.grid.querySelectorAll(':scope > [data-product-id]')].map((element) => {
          const tags = JSON.parse(element.dataset.tags || '[]');
          const categories = JSON.parse(element.dataset.categories || 'null') || [];
          return { element, tags: new Set((categories.length ? categories : tags).map(canonical)), search: normalize(element.dataset.search + ' ' + tags.join(' ')) };
        });
        this.products.sort((a, b) => {
          const ai = sourceOrder.indexOf(Number(a.element.dataset.sourceUrl?.match(/\/(\d+)-/)?.[1]));
          const bi = sourceOrder.indexOf(Number(b.element.dataset.sourceUrl?.match(/\/(\d+)-/)?.[1]));
          return (ai < 0 ? sourceOrder.length : ai) - (bi < 0 ? sourceOrder.length : bi);
        });
        this.products.forEach(({ element }) => this.grid.append(element));
        this.checkboxes.forEach((input) => { input.disabled = false; });
        this.readURL();
        this.bindEvents();
        this.render();
        this.dataset.ready = '';
      } catch (error) {
        this.status.classList.remove('visually-hidden');
        this.status.textContent = 'The product filters could not load. Please reload the page to try again.';
        this.querySelectorAll('.product-grid__item').forEach((element) => { element.style.display = 'block'; });
        console.error('Beam catalogue loading failed', error);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async loadCatalogue() {
      const current = new URL(location.href);
      const base = new URL(this.dataset.baseUrl, location.origin);
      const fetchPage = async (url) => {
        url.searchParams.set('section_id', this.dataset.sectionId);
        const preview = current.searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Catalogue request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const catalogue = doc.querySelector('beam-catalogue');
        if (!catalogue) throw new Error('Catalogue missing');
        return catalogue;
      };
      let next = this.querySelector('[data-catalogue-next]')?.dataset.catalogueNext;
      if (current.pathname !== base.pathname || current.searchParams.has('page')) {
        const catalogue = await fetchPage(base);
        this.querySelector('#ResultsList').replaceWith(catalogue.querySelector('#ResultsList'));
        this.grid = this.querySelector('.product-grid');
        this.querySelector('.beam-catalogue-tools select').replaceWith(catalogue.querySelector('.beam-catalogue-tools select'));
        next = catalogue.querySelector('[data-catalogue-next]')?.dataset.catalogueNext;
      }
      const visited = new Set();
      while (next) {
        if (visited.has(next)) throw new Error('Repeated catalogue page');
        visited.add(next);
        const catalogue = await fetchPage(new URL(next, location.origin));
        this.grid.append(...catalogue.querySelectorAll('.product-grid > [data-product-id]'));
        next = catalogue.querySelector('[data-catalogue-next]')?.dataset.catalogueNext;
      }
      const seen = new Set();
      this.grid.querySelectorAll(':scope > [data-product-id]').forEach((element) => {
        if (seen.has(element.dataset.productId)) element.remove();
        else seen.add(element.dataset.productId);
      });
    }

    readURL() {
      const url = new URL(location.href);
      const tag = decodeURIComponent(url.pathname.slice(this.dataset.baseUrl.length + 1));
      this.checkboxes.forEach((input) => {
        input.checked = url.searchParams.getAll(input.name).some((value) => canonical(value) === canonical(input.value)) || tag.split('+').includes(slug(input.value));
      });
      this.searchInput.value = url.searchParams.get('q') || '';
      this.query = this.searchInput.value;
      const limit = url.searchParams.get('limit');
      this.limit.value = ['12', '24', '36', 'all'].includes(limit) ? limit : '12';
      this.page = Math.max(1, Number(url.searchParams.get('catalogue_page')) || 1);
      if (this.checkboxes.some((input) => input.checked)) this.details.open = true;
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-filter-group], [data-catalogue-limit]')) return;
        this.page = 1;
        this.render();
        this.writeURL();
      });
      this.querySelector('.beam-catalogue-tools form').addEventListener('submit', (event) => {
        event.preventDefault();
        this.query = this.searchInput.value.trim();
        this.page = 1;
        this.render();
        this.writeURL();
      });
      this.querySelector('[data-catalogue-clear]').addEventListener('click', (event) => {
        event.preventDefault();
        this.checkboxes.forEach((input) => { input.checked = false; });
        this.searchInput.value = this.query = '';
        this.page = 1;
        this.render();
        this.writeURL();
      });
      this.pages.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-page]');
        if (!button || button.disabled) return;
        this.page = Number(button.dataset.page);
        this.render();
        this.writeURL();
        this.grid.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
      this.onPopState = () => { this.readURL(); this.render(); };
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    writeURL() {
      const url = new URL(location.href);
      url.pathname = this.dataset.baseUrl;
      groups.forEach((group) => url.searchParams.delete('beam_' + group));
      this.checkboxes.filter((input) => input.checked).forEach((input) => url.searchParams.append(input.name, input.value));
      url.searchParams.delete('page');
      for (const [key, value] of [['q', this.query], ['limit', this.limit.value === '12' ? '' : this.limit.value], ['catalogue_page', this.page > 1 ? this.page : '']]) {
        if (value) url.searchParams.set(key, value);
        else url.searchParams.delete(key);
      }
      history.pushState({}, '', url);
    }

    render() {
      const selected = Object.fromEntries(groups.map((group) => [group, this.checkboxes.filter((input) => input.dataset.filterGroup === group && input.checked).map((input) => canonical(input.value))]));
      const words = normalize(this.query).replace(/^"|"$/g, '').split(/\s+/).filter(Boolean);
      const matches = (product, ignoreGroup) => words.every((word) => product.search.includes(word)) && groups.every((group) => group === ignoreGroup || !selected[group].length || selected[group].some((tag) => product.tags.has(tag)));
      const filtered = this.products.filter((product) => matches(product));
      const count = this.limit.value === 'all' ? Math.max(1, filtered.length) : Number(this.limit.value);
      const pageCount = Math.max(1, Math.ceil(filtered.length / count));
      this.page = Math.min(this.page, pageCount);
      const visible = new Set(filtered.slice((this.page - 1) * count, this.page * count));
      this.products.forEach((product) => { product.element.hidden = !visible.has(product); });
      this.querySelector('[data-catalogue-empty]').hidden = filtered.length !== 0;
      this.status.textContent = `${filtered.length} products found. Page ${this.page} of ${pageCount}.`;
      this.querySelector('[data-catalogue-pagination]').hidden = filtered.length === 0;
      this.pages.replaceChildren();
      const addPage = (text, page, disabled = false) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = text;
        button.dataset.page = page;
        button.disabled = disabled;
        if (text === String(this.page)) button.setAttribute('aria-current', 'page');
        this.pages.append(button);
      };
      addPage('Prev', this.page - 1, this.page === 1);
      for (let page = 1; page <= pageCount; page++) addPage(String(page), page);
      addPage('Next', this.page + 1, this.page === pageCount);
    }
  }
  if (!customElements.get('beam-catalogue')) customElements.define('beam-catalogue', BeamCatalogue);
})();

